import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';


import { environment } from 'src/environments/environment';

import { BaseRequest } from 'src/app/model/api/request/BaseRequest';
import { BaseResponse } from 'src/app/model/api/response/BaseResponse';


import { ApiError } from './ApiError';


function apiPath(relativePath: string): string {
  if (!!environment.apiEndpoint) {
    return environment.apiEndpoint + relativePath;
  }

  return relativePath;
}

function isSuccessful(response: BaseResponse): boolean {
  return response.code === 200;
}


/**
 * This service helps fire restful API calls.
 *
 * Access Token is injected into request header (automatically).
 */
@Injectable({
  providedIn: 'root',
})
export class ApiCallerService {
  private makeHttpOptions(token?: string) {
    const headerEntries:any = {};

    // Note: Authorization header is automatically injected by MsalInterceptor
    // The interceptor handles token acquisition and injection for protected resources

    if (localStorage['lang']) {
      // Inject user's preferred language to HTTP request header
      headerEntries['Accept-Language'] = localStorage['lang'];
    }

    return {
      headers: new HttpHeaders(headerEntries),
    };
  }

  constructor(protected http: HttpClient) {}

  /**
   * Note: Caller should handle the potential error (exception) thrown
   */
  get<TResponse extends BaseResponse>(
    relativePath: string,
    params?: HttpParams
  ): Observable<TResponse> {
    const httpOptions = {
      ...this.makeHttpOptions(/* TODO token ..*/),
      params,
    };

    return this.http.get<TResponse>(apiPath(relativePath), httpOptions).pipe(
      map((response: TResponse) => {
        if (response && response.code && !isSuccessful(response)) {
          throw new ApiError(response.message, response);
        }
        return response;
      })
    );
  }

  /**
   * Note: Caller should handle the potential error (exception) thrown
   */
  getBlob(
    relativePath: string,
    params?: HttpParams
  ): Observable<Blob> {
    return this.http.get(apiPath(relativePath), {
      ...this.makeHttpOptions(/* TODO token ..*/),
      params,
      responseType: 'blob',
    });
  }

  /**
   * Note: Caller should handle the potential error (exception) thrown
   */
  post<TResponse extends BaseResponse>(
    relativePath: string,
    body: BaseRequest | FormData | null
  ): Observable<TResponse> {
    const httpOptions = this.makeHttpOptions(/* TODO token ..*/);

    return this.http
      .post<TResponse>(apiPath(relativePath), body, httpOptions)
      .pipe(
        map((response: TResponse) => {
          if (response && response.code && !isSuccessful(response)) {
            throw new ApiError(response.message, response);
          }
          return response;
        })
      );
  }

  /**
   * Note: Caller should handle the potential error (exception) thrown
   */
  put<TResponse extends BaseResponse>(
    relativePath: string,
    body: BaseRequest | FormData | null
  ): Observable<TResponse> {
    const httpOptions = this.makeHttpOptions(/* TODO token ..*/);

    return this.http
      .put<TResponse>(apiPath(relativePath), body, httpOptions)
      .pipe(
        map((response: TResponse) => {
          if (response && response.code && !isSuccessful(response)  ) {
            throw new ApiError(response.message, response);
          }
          return response;
        })
      );
  }

  /**
   * Note: Caller should handle the potential error (exception) thrown
   */
  delete<TResponse extends BaseResponse>(
    relativePath: string,
    params?: HttpParams
  ): Observable<TResponse> {
    const httpOptions = {
      ...this.makeHttpOptions(/* TODO token ..*/),
      params,
    };
    return this.http.delete<TResponse>(apiPath(relativePath), httpOptions).pipe(
      map((response: TResponse) => {
        if (response && response.code && !isSuccessful(response)) {
          throw new ApiError(response.message, response);
        }
        return response;
      })
    );
  }
}
